import React, {useEffect, useMemo, useState} from 'react';
import {
    App,
    Button,
    Card,
    DatePicker,
    Descriptions,
    Drawer,
    Form,
    Input,
    Modal,
    Select,
    Space,
    Table,
    Tag,
    Timeline,
    Typography
} from 'antd';
import type {ColumnsType} from 'antd/es/table';
import {ReloadOutlined, SearchOutlined, SecurityScanOutlined} from '@ant-design/icons';
import dayjs, {Dayjs} from 'dayjs';
import {useSearchParams} from 'react-router-dom';
import {
    AgentOutputValidationDetail,
    AgentOutputValidationRecord,
    agentOutputValidationService
} from '../../services/agentOutputValidationService';

const {RangePicker} = DatePicker;
const {TextArea} = Input;
const {Title, Text} = Typography;

interface FilterValues {
    agentName?: string;
    status?: string;
    outputKind?: string;
    outputCode?: string;
    traceId?: string;
    timeRange?: [Dayjs, Dayjs];
}

const statusColorMap: Record<string, string> = {
    PASSED: 'success',
    REPAIRED: 'processing',
    FAILED: 'error'
};

const AgentOutputValidationPage: React.FC = () => {
    const {message} = App.useApp();
    const [searchParams, setSearchParams] = useSearchParams();
    const [form] = Form.useForm<FilterValues>();
    const [loading, setLoading] = useState(false);
    const [records, setRecords] = useState<AgentOutputValidationRecord[]>([]);
    const [page, setPage] = useState(0);
    const [size, setSize] = useState(20);
    const [total, setTotal] = useState(0);
    const [detailOpen, setDetailOpen] = useState(false);
    const [detailLoading, setDetailLoading] = useState(false);
    const [detail, setDetail] = useState<AgentOutputValidationDetail | null>(null);
    const [timelineOpen, setTimelineOpen] = useState(false);
    const [timelineLoading, setTimelineLoading] = useState(false);
    const [timeline, setTimeline] = useState<AgentOutputValidationDetail[]>([]);

    useEffect(() => {
        const initialAgentName = searchParams.get('agentName') || undefined;
        if (initialAgentName) {
            form.setFieldsValue({agentName: initialAgentName});
        }
        void loadPage(0, size, form.getFieldsValue());
    }, []);

    const columns: ColumnsType<AgentOutputValidationRecord> = useMemo(() => [
        {
            title: '时间',
            dataIndex: 'createdAt',
            key: 'createdAt',
            width: 180,
            render: (value?: string) => value ? dayjs(value).format('YYYY-MM-DD HH:mm:ss') : '-'
        },
        {
            title: '智能体',
            dataIndex: 'agentName',
            key: 'agentName',
            width: 160,
            render: (value?: string) => value || '-'
        },
        {
            title: '产出类型',
            dataIndex: 'outputKind',
            key: 'outputKind',
            width: 120,
            render: (value?: string) => value ? <Tag>{value}</Tag> : '-'
        },
        {
            title: '产出编码',
            dataIndex: 'outputCode',
            key: 'outputCode',
            width: 180,
            render: (value?: string) => value ? <Text code>{value}</Text> : '-'
        },
        {
            title: '状态',
            dataIndex: 'status',
            key: 'status',
            width: 120,
            render: (value?: string) => value ? <Tag color={statusColorMap[value] || 'default'}>{value}</Tag> : '-'
        },
        {
            title: 'Checker',
            dataIndex: 'checkerName',
            key: 'checkerName',
            width: 180,
            render: (value?: string) => value || '-'
        },
        {
            title: '修复轮次',
            dataIndex: 'repairAttempt',
            key: 'repairAttempt',
            width: 100,
            render: (value?: number) => value ?? 0
        },
        {
            title: '违规数',
            dataIndex: 'violationCount',
            key: 'violationCount',
            width: 100,
            render: (value?: number) => value ?? 0
        },
        {
            title: '操作',
            key: 'action',
            width: 180,
            fixed: 'right',
            render: (_, record) => (
                <Space size="small">
                    <Button size="small" onClick={() => void openDetail(record.id)}>详情</Button>
                    <Button size="small" onClick={() => void openTimeline(record.traceId)} disabled={!record.traceId}>
                        时间线
                    </Button>
                </Space>
            )
        }
    ], []);

    const loadPage = async (targetPage = page, targetSize = size, values?: FilterValues) => {
        setLoading(true);
        try {
            const queryValues = values || form.getFieldsValue();
            const range = queryValues.timeRange;
            const data = await agentOutputValidationService.getPage({
                agentName: queryValues.agentName,
                status: queryValues.status,
                outputKind: queryValues.outputKind,
                outputCode: queryValues.outputCode,
                traceId: queryValues.traceId,
                startTime: range?.[0]?.startOf('minute').toISOString(),
                endTime: range?.[1]?.endOf('minute').toISOString(),
                page: targetPage,
                size: targetSize,
            });
            setRecords(data.content || []);
            setPage(data.number || targetPage);
            setSize(data.size || targetSize);
            setTotal(data.totalElements || 0);
        } catch (error: any) {
            message.error(error?.message || '加载校验记录失败');
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = async () => {
        const values = form.getFieldsValue();
        setSearchParams(values.agentName ? {agentName: values.agentName} : {});
        await loadPage(0, size, values);
    };

    const handleReset = async () => {
        form.resetFields();
        setSearchParams({});
        await loadPage(0, size, {});
    };

    const openDetail = async (id: number) => {
        setDetailOpen(true);
        setDetailLoading(true);
        try {
            const data = await agentOutputValidationService.getDetail(id);
            setDetail(data);
        } catch (error: any) {
            message.error(error?.message || '加载详情失败');
        } finally {
            setDetailLoading(false);
        }
    };

    const openTimeline = async (traceId?: string) => {
        if (!traceId) {
            return;
        }
        setTimelineOpen(true);
        setTimelineLoading(true);
        try {
            const data = await agentOutputValidationService.getTraceTimeline(traceId);
            setTimeline(data);
        } catch (error: any) {
            message.error(error?.message || '加载时间线失败');
        } finally {
            setTimelineLoading(false);
        }
    };

    return (
        <div style={{padding: 24}}>
            <Card bordered={false}>
                <Space direction="vertical" size={16} style={{width: '100%'}}>
                    <Space align="center">
                        <SecurityScanOutlined/>
                        <Title level={4} style={{margin: 0}}>产出校验记录</Title>
                    </Space>

                    <Form form={form} layout="inline">
                        <Form.Item name="agentName" label="智能体">
                            <Input placeholder="请输入智能体名称" allowClear style={{width: 180}}/>
                        </Form.Item>
                        <Form.Item name="status" label="状态">
                            <Select
                                allowClear
                                placeholder="全部状态"
                                style={{width: 140}}
                                options={[
                                    {value: 'PASSED', label: 'PASSED'},
                                    {value: 'REPAIRED', label: 'REPAIRED'},
                                    {value: 'FAILED', label: 'FAILED'}
                                ]}
                            />
                        </Form.Item>
                        <Form.Item name="outputKind" label="产出类型">
                            <Select
                                allowClear
                                placeholder="全部类型"
                                style={{width: 140}}
                                options={[
                                    {value: 'MESSAGE', label: 'MESSAGE'},
                                    {value: 'EVENT', label: 'EVENT'}
                                ]}
                            />
                        </Form.Item>
                        <Form.Item name="outputCode" label="产出编码">
                            <Input placeholder="如 TRADE_PROPOSAL" allowClear style={{width: 200}}/>
                        </Form.Item>
                        <Form.Item name="traceId" label="TraceId">
                            <Input placeholder="请输入 TraceId" allowClear style={{width: 220}}/>
                        </Form.Item>
                        <Form.Item name="timeRange" label="时间范围">
                            <RangePicker showTime allowClear/>
                        </Form.Item>
                        <Form.Item>
                            <Space>
                                <Button type="primary" icon={<SearchOutlined/>} onClick={() => void handleSearch()}>
                                    查询
                                </Button>
                                <Button icon={<ReloadOutlined/>} onClick={() => void handleReset()}>
                                    重置
                                </Button>
                            </Space>
                        </Form.Item>
                    </Form>

                    <Table
                        rowKey="id"
                        loading={loading}
                        columns={columns}
                        dataSource={records}
                        scroll={{x: 1300}}
                        pagination={{
                            current: page + 1,
                            pageSize: size,
                            total,
                            showSizeChanger: true,
                            showQuickJumper: true,
                            showTotal: (value) => `共 ${value} 条`,
                            onChange: (current, pageSize) => {
                                void loadPage(current - 1, pageSize, form.getFieldsValue());
                            }
                        }}
                    />
                </Space>
            </Card>

            <Drawer
                title="校验记录详情"
                width={720}
                open={detailOpen}
                onClose={() => setDetailOpen(false)}
                loading={detailLoading}
            >
                {detail && (
                    <Space direction="vertical" size={16} style={{width: '100%'}}>
                        <Descriptions bordered size="small" column={2}>
                            <Descriptions.Item label="TraceId" span={2}>{detail.traceId || '-'}</Descriptions.Item>
                            <Descriptions.Item label="智能体">{detail.agentName || '-'}</Descriptions.Item>
                            <Descriptions.Item label="状态">
                                {detail.status ?
                                    <Tag color={statusColorMap[detail.status] || 'default'}>{detail.status}</Tag> : '-'}
                            </Descriptions.Item>
                            <Descriptions.Item label="产出类型">{detail.outputKind || '-'}</Descriptions.Item>
                            <Descriptions.Item label="产出编码">{detail.outputCode || '-'}</Descriptions.Item>
                            <Descriptions.Item label="Checker">{detail.checkerName || '-'}</Descriptions.Item>
                            <Descriptions.Item label="修复轮次">{detail.repairAttempt ?? 0}</Descriptions.Item>
                            <Descriptions.Item label="违规数">{detail.violationCount ?? 0}</Descriptions.Item>
                            <Descriptions.Item
                                label="时间">{detail.createdAt ? dayjs(detail.createdAt).format('YYYY-MM-DD HH:mm:ss') : '-'}</Descriptions.Item>
                        </Descriptions>

                        <div>
                            <Text strong>原始 JSON</Text>
                            <TextArea readOnly value={detail.rawOutputJson || ''} autoSize={{minRows: 6, maxRows: 12}}/>
                        </div>

                        <div>
                            <Text strong>归一化 JSON</Text>
                            <TextArea readOnly value={detail.normalizedOutputJson || ''}
                                      autoSize={{minRows: 6, maxRows: 12}}/>
                        </div>

                        <div>
                            <Text strong>反馈 Prompt</Text>
                            <TextArea readOnly value={detail.feedbackPrompt || ''}
                                      autoSize={{minRows: 4, maxRows: 10}}/>
                        </div>

                        <div>
                            <Text strong>违规项</Text>
                            <Table
                                rowKey={(record, index) => `${record.fieldPath}-${index}`}
                                dataSource={detail.violations || []}
                                pagination={false}
                                size="small"
                                columns={[
                                    {title: '字段', dataIndex: 'fieldPath', key: 'fieldPath', width: 180},
                                    {title: '错误码', dataIndex: 'errorCode', key: 'errorCode', width: 140},
                                    {title: '说明', dataIndex: 'message', key: 'message'},
                                    {title: '期望', dataIndex: 'expected', key: 'expected', width: 160},
                                    {title: '实际', dataIndex: 'actual', key: 'actual', width: 160},
                                ]}
                                scroll={{x: 900}}
                            />
                        </div>
                    </Space>
                )}
            </Drawer>

            <Modal
                title="Trace 时间线"
                open={timelineOpen}
                onCancel={() => setTimelineOpen(false)}
                footer={null}
                width={900}
                confirmLoading={timelineLoading}
            >
                <Timeline
                    items={timeline.map(item => ({
                        color: item.status === 'FAILED' ? 'red' : item.status === 'REPAIRED' ? 'blue' : 'green',
                        children: (
                            <Space direction="vertical" size={4} style={{width: '100%'}}>
                                <Space wrap>
                                    <Tag
                                        color={statusColorMap[item.status || ''] || 'default'}>{item.status || '-'}</Tag>
                                    <Text code>{item.outputKind || '-'}/{item.outputCode || '-'}</Text>
                                    <Text>{item.checkerName || '-'}</Text>
                                    <Text type="secondary">
                                        {item.createdAt ? dayjs(item.createdAt).format('YYYY-MM-DD HH:mm:ss') : '-'}
                                    </Text>
                                </Space>
                                <Text>{item.feedbackPrompt || item.normalizedOutputJson || '-'}</Text>
                            </Space>
                        )
                    }))}
                />
            </Modal>
        </div>
    );
};

export default AgentOutputValidationPage;
